/**
 * **词语表导入预览的「词卡网格」**（2026-10-23）。
 *
 * ## 用户原话（验收标准）
 *
 * > "词语表预览**从来不列词**……导入时**看起来一片空白**。"
 *
 * 他同意加一个网格，但**只展示**：⛔ 没有可点浮窗、⛔ 没有跳转、⛔ 没有朗读；
 * 也⛔ 不要把 2026-10-21 删掉的那两块加回来（「会新增的 N 条（点开看逐行）」、
 * 「⚠ 有问题的行（N）」的逐条明细）—— 那两块与本网格无关，别顺手复原。
 *
 * ## 长什么样
 *
 * ```
 * ┌──────────────┐  ┌──────────────┐
 * │   kàn jiàn   │  │   chūn tiān  │   ← 拼音在上（小字、等宽）
 * │     看见     │  │     春天     │   ← 词在下（大字、加粗）
 * └──────────────┘  └──────────────┘
 * ```
 *
 *   · **拼音在上、词在下** —— 与字卡（`HanziCard`）同一种排法，用户明确要求；
 *   · 列数：**手机 2 列 / 小屏 4 列 / 桌面 6 列**（2026-10-23 用户要求从 4 列加到 6 列：
 *     一行只放 4 个太稀；8 列会把 `yī dòng bù dòng` 这种长拼音挤断，6 列刚好）；
 *   · 视觉语言复用字卡：`rounded-xl` 圆角 + `border-slate-200` 边框 + 白底，
 *     分组块用**浅灰底**（`bg-slate-50/60`）把「一行」圈起来；
 *   · ⛔ **没有**字卡那几种状态底色（玫瑰 / 浅绿 / 浅橙 / 琥珀）：词没有「? 待确认」
 *     这件事，也没有"多音字标注"，给底色只会凭空造出五种含义；
 *   · 词再多也**一次全显示**（225 个也一样），不分页、不做「再显示 100 个」。
 *
 * ## 分组
 *
 * 一行 = 一块，块标题逐字 `第 N 行（X 个词）`（不是「个字」）：
 * 与字卡网格同一个分组实现（`groupByLine`，这里传 `unit: '个词'`），
 * 只是条目换成了词。块按行号升序、组内按粘贴顺序。
 *
 * ⚠️ 数据怎么来的（报告 vs 原文）见 `importPreviewWordCards.ts`；本文件只负责画。
 */
import { useMemo } from 'react';
import type { ImportReportView } from '../../lib/api/dictation';
import {
  buildWordCardLines,
  countWordCards,
  describeWordPreviewTitle,
  type WordCardItem,
} from './importPreviewWordCards';

/**
 * 词网格的列数（**只有这一份**）。
 *
 * 手机 2 列 → 小屏 4 列 → 桌面 6 列（2026-10-23 用户要求）：
 * 原来 2 / 3 / 4 一行只有 4 个，太稀；8 列会把 `yī dòng bù dòng` 这种长拼音挤断，
 * 6 列刚好。字表那张网格（手机 3 / 小屏 6 / 桌面 8）**不要动**（`HANZI_GRID_CLASS`）。
 */
export const WORD_GRID_CLASS = 'grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6';

/** 一张词卡：**只展示**，不是按钮（没有浮窗、没有跳转）。 */
export function WordCard({ word }: { word: WordCardItem }) {
  const pinyin = word.pinyin === '' ? '（没有读音）' : word.pinyin;
  return (
    <li className="flex min-w-0">
      <div
        /** 给测试与样式一个稳定钩子（词卡是纯展示，没有状态属性）。 */
        data-word-card="true"
        className="flex min-h-[44px] w-full min-w-0 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-center"
      >
        {/*
          拼音在上：与字卡同一种排法（用户 2026-10-23 明确要求"拼音在上，词在下"）。
          用 `min-h-4` 而不是固定高度：宁可让卡片高一点，也不要把多音节拼音截掉。
        */}
        <span className="block min-h-4 w-full break-words font-mono text-xs leading-4 text-slate-600">
          {pinyin}
        </span>
        {/* 词在下：视觉主体。字号 15px（单字卡是 18px；6 列也放得下），四字成语可折行、不截断。 */}
        <span className="block w-full break-words text-[15px] font-semibold leading-6 text-slate-900">
          {word.wordText}
        </span>
      </div>
    </li>
  );
}

export interface WordCardGridProps {
  /** 预览 / 提交报告（读里面的词行）；`null` = 还没预览。 */
  report: ImportReportView | null;
  /** 粘贴框里的原文（同一份解析器还原行号、行内顺序与整词拼音）。 */
  text: string;
  /** 文本里没写课本时的默认课本（与发预览请求时那份一致）。 */
  defaultTextbook?: string;
}

/**
 * 词语表预览里的词卡网格。
 *
 * ⚠️ 一个词都没有时**整块不渲染**（返回 `null`）：不要在预览里画一个空壳标题，
 *    那正是用户抱怨的"看起来一片空白"。
 */
export function WordCardGrid({ report, text, defaultTextbook = '' }: WordCardGridProps) {
  const lines = useMemo(
    () => buildWordCardLines({ report, text, defaultTextbook }),
    [report, text, defaultTextbook],
  );
  const total = countWordCards(lines);

  if (lines.length === 0) return null;

  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
        {/* 顶部那句：**逐字**按用户给的文案（见 `describeWordPreviewTitle`）。 */}
        <p className="text-sm font-semibold text-slate-800">
          {describeWordPreviewTitle(total, lines.length)}
        </p>

        <div className="mt-3 space-y-4">
          {lines.map((line) => (
            <section
              key={line.key}
              /** 测试与实际核对都用它：这一块的原文行号。 */
              data-word-line={line.line}
              aria-label={line.title}
              className="rounded-xl border border-slate-200 bg-slate-50/60 px-2 py-2"
            >
              <h4 className="mb-1.5 px-0.5 text-sm font-semibold text-slate-800">{line.title}</h4>
              <ul className={WORD_GRID_CLASS}>
                {line.positions.map((word) => (
                  <WordCard key={`${word.line}-${word.indexInLine}`} word={word} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
